// Staff portal entry point: sign-in gate, role-based navigation, hash routing.
import { initAuth, currentSession, signOut } from './auth.js';
import { renderLogin } from './login.js';
import { esc, toast } from './ui.js';
import { api } from './api.js';
import { bookingsView } from './views/bookings.js';
import { experiencesView } from './views/experiences.js';
import { guestsView } from './views/guests.js';
import { galleriesView } from './views/galleries.js';
import { emailsView } from './views/emails.js';
import { analyticsView, auditView, staffView } from './views/reports.js';

const MGR = ['Owner', 'Manager'];
// The server enforces every one of these; the menu just hides what a role can't use.
const ROUTES = [
  { path: 'bookings', label: 'Bookings', roles: [...MGR, 'Guide'], view: bookingsView },
  { path: 'experiences', label: 'Experiences & dates', roles: [...MGR, 'ContentEditor'], view: experiencesView },
  { path: 'guests', label: 'Guests', roles: MGR, view: guestsView },
  { path: 'galleries', label: 'Galleries & media', roles: [...MGR, 'ContentEditor'], view: galleriesView },
  { path: 'emails', label: 'Automated emails', roles: MGR, view: emailsView },
  { path: 'analytics', label: 'Analytics', roles: MGR, view: analyticsView },
  { path: 'staff', label: 'Staff & roles', roles: MGR, view: staffView },
  { path: 'audit', label: 'Audit log', roles: ['Owner'], view: auditView },
];

const app = document.getElementById('app');
let me = null;

async function start() {
  try {
    await initAuth();
  } catch {
    app.innerHTML = '<main class="login"><p class="notice error">The portal could not load its settings. Please refresh.</p></main>';
    return;
  }
  const session = await currentSession();
  if (!session) return renderLogin(app, start);
  try {
    me = await api('/me');
  } catch (e) {
    signOut();
    return renderLogin(app, start, e.message);
  }
  me.allowed = ROUTES.filter((r) => r.roles.some((role) => me.groups.includes(role)));
  shell();
  route();
}

function shell() {
  const label = me.role === 'ContentEditor' ? 'Content editor' : me.role;
  app.innerHTML = `<div class="portal">
    <nav class="portal-nav" aria-label="Portal">
      <a class="brand" href="/" title="View the website" style="flex-direction:column;align-items:flex-start;gap:6px">
        <img class="logo" src="/assets/logo-full.svg" alt="Magnolia &amp; Oak" width="1100" height="685">
        <span class="eyebrow" style="font-size:10px">Staff portal</span></a>
      ${me.allowed.map((r) => `<a href="#/${r.path}" data-path="${r.path}">${esc(r.label)}</a>`).join('')}
      <div class="me"><span>${esc(me.email)}</span><span class="role-badge">${esc(label)}</span>
        <button class="btn small" type="button" id="signout">Sign out</button></div>
    </nav>
    <main class="portal-main" id="view" tabindex="-1"></main>
  </div>`;
  document.getElementById('signout').addEventListener('click', () => { signOut(); location.hash = ''; renderLogin(app, start, 'You signed out.'); });
}

async function route() {
  if (!me) return;
  const path = location.hash.replace(/^#\/?/, '') || me.allowed[0]?.path;
  const r = me.allowed.find((x) => x.path === path) || me.allowed[0];
  if (!r) { document.getElementById('view').innerHTML = '<p class="notice">Your role has no portal pages yet.</p>'; return; }
  document.querySelectorAll('.portal-nav a[data-path]').forEach((a) => a.setAttribute('aria-current', a.dataset.path === r.path ? 'page' : 'false'));
  // A fresh container per page so one page's event listeners never fire on another.
  const view = document.getElementById('view');
  const root = document.createElement('div');
  root.style.cssText = 'display:flex;flex-direction:column;gap:24px';
  view.replaceChildren(root);
  root.innerHTML = '<p class="muted">Loading…</p>';
  document.title = `${r.label} | Magnolia & Oak staff`;
  try {
    await r.view(root, me);
  } catch (e) {
    root.innerHTML = `<p class="notice error">${esc(e.message || 'Something went wrong loading this page.')}</p>`;
    if (e.status !== 401) toast(e.message, 'error');
  }
  view.focus({ preventScroll: true });
}

window.addEventListener('hashchange', route);
start();
